import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import {
  blockFor, blockedCallerKey, untilFirstEnds, type BlockCallerInput, type BlockTarget, type BlockedCaller,
} from "@/lib/blocked-callers";
import { useKeys } from "@/lib/queries";

/**
 * core-api's `/blocked-callers` (ADR-159 in platform). Everyone signed in reads the list and
 * blocks; supervisors and above block for good and unblock.
 *
 * Only Settings reads the whole list. core-api keeps 2,000 blocks a tenant at most, and blocks for
 * good only add up, while a contact, a call or a missed call needs to know about its own caller
 * only: those ask about it (`?number=`), and a missed-calls list asks once for all its rows.
 *
 * A block ends by itself, so whatever shows one asks again when it ends (`untilFirstEnds`), and
 * "Blocked" goes from the screen with it.
 */

type Caller = Pick<BlockTarget, "number" | "hidden">;

/** Every block in force, newest first. */
export function useBlockedCallers(options: { enabled?: boolean } = {}) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.blockedCallers(),
    queryFn: () => api.get<BlockedCaller[]>("/blocked-callers"),
    enabled: options.enabled ?? true,
    staleTime: 30_000,
    refetchInterval: (query) => untilFirstEnds(query.state.data),
  });
}

/** The blocks in force on these callers, in one request. Nothing is asked when none can be blocked. */
export function useBlocksOn(targets: readonly (Caller | null)[]) {
  const keys = useKeys();
  const callers = [...new Set(targets.map((t) => (t ? blockedCallerKey(t) : null)).filter((c) => c !== null))].sort();
  return useQuery({
    queryKey: keys.blocksOn(callers),
    queryFn: () =>
      api.get<BlockedCaller[]>(`/blocked-callers?${new URLSearchParams(callers.map((c) => ["number", c]))}`),
    enabled: callers.length > 0,
    staleTime: 30_000,
    // A list whose rows change keeps what it knew until the new answer: a block is found by its
    // number, so the last answer can only be right about the callers both lists share.
    placeholderData: (previous) => previous,
    refetchInterval: (query) => untilFirstEnds(query.state.data),
  });
}

/** The block in force on one caller, if there is one. */
export function useBlockOn(target: Caller | null): BlockedCaller | undefined {
  const blocks = useBlocksOn([target]);
  return target ? blockFor(blocks.data, target) : undefined;
}

function useRefetchBlockedCallers() {
  const keys = useKeys();
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: keys.blockedCallers() });
}

/** Block a caller. A caller already blocked for longer stays as they were; the answer says so. */
export function useBlockCaller() {
  const refetch = useRefetchBlockedCallers();
  return useMutation({
    mutationFn: (input: BlockCallerInput) => api.post<BlockedCaller>("/blocked-callers", input),
    onSuccess: refetch,
  });
}

/** End a block before its time. */
export function useUnblockCaller() {
  const refetch = useRefetchBlockedCallers();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/blocked-callers/${id}`),
    onSuccess: refetch,
  });
}
